import { useCallback, useEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { showError, showSuccess } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { useCanEditCompany } from '@/hooks/use-permissions'

type InputType = 'text' | 'number'

interface InlineEditableCellProps {
  primaryOwnerId?: string | null
  secondaryOwnerId?: string | null
  value: string | number | null | undefined
  inputType?: InputType
  placeholder?: string
  formatDisplay?: (value: string | number | null | undefined) => string
  onSave: (value: string | number | null) => Promise<void>
  successMessage?: string
  errorMessage?: string
}

function parseValue(raw: string, inputType: InputType): string | number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  if (inputType === 'number') {
    const parsed = Number(trimmed.replace(/,/g, ''))
    if (!Number.isFinite(parsed)) {
      throw new Error('Please enter a valid number.')
    }
    return parsed
  }

  return trimmed
}

function InlineEditableCellBase({
  primaryOwnerId,
  secondaryOwnerId,
  value,
  inputType = 'text',
  placeholder = '—',
  formatDisplay,
  onSave,
  successMessage,
  errorMessage,
}: InlineEditableCellProps) {
  const canEditCompany = useCanEditCompany()
  const canEdit = canEditCompany(primaryOwnerId, secondaryOwnerId)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [pendingValue, setPendingValue] = useState<
    string | number | null | undefined
  >(undefined)
  const inputRef = useRef<HTMLInputElement>(null)
  const savingRef = useRef(false)

  const displayedValue =
    pendingValue !== undefined && isSaving ? pendingValue : value

  const displayText = formatDisplay
    ? formatDisplay(displayedValue)
    : displayedValue === null ||
        displayedValue === undefined ||
        displayedValue === ''
      ? placeholder
      : String(displayedValue)

  const isEmpty =
    displayedValue === null ||
    displayedValue === undefined ||
    displayedValue === ''

  const displayClassName = cn(
    'truncate text-xs font-univers font-normal text-primary',
    isEmpty && 'text-muted-foreground',
    inputType === 'number' && 'tabular-nums'
  )

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  const handleStartEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setEditValue(value === null || value === undefined ? '' : String(value))
      setIsEditing(true)
    },
    [value]
  )

  const handleSave = useCallback(async () => {
    if (savingRef.current) return
    setIsEditing(false)

    let parsedValue: string | number | null
    try {
      parsedValue = parseValue(editValue, inputType)
    } catch (error) {
      showError(error, 'Please enter a valid number')
      return
    }

    const current = value ?? null
    if (parsedValue === current) return

    savingRef.current = true
    setIsSaving(true)
    setPendingValue(parsedValue)

    try {
      await onSave(parsedValue)
      if (successMessage) showSuccess(successMessage)
    } catch (error) {
      showError(error, errorMessage ?? 'Failed to update value')
    } finally {
      savingRef.current = false
      setIsSaving(false)
      setPendingValue(undefined)
    }
  }, [editValue, inputType, onSave, value, successMessage, errorMessage])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        void handleSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setIsEditing(false)
      }
    },
    [handleSave]
  )

  if (!canEdit) {
    return (
      <span className={displayClassName}>{displayText || placeholder}</span>
    )
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={() => void handleSave()}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'w-full bg-transparent outline-none',
          'text-xs font-univers font-normal text-primary',
          'border-b border-primary pb-0.5',
          inputType === 'number' && 'tabular-nums',
          '[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [appearance:textfield]'
        )}
        inputMode={inputType === 'number' ? 'decimal' : undefined}
      />
    )
  }

  return (
    <div className="relative -m-2">
      <Button
        type="button"
        variant="ghost"
        onClick={handleStartEdit}
        disabled={isSaving}
        className={cn(
          'group flex h-auto w-full items-center justify-between rounded-none p-2 text-left',
          'hover:bg-muted/50'
        )}
      >
        <span className={displayClassName}>{displayText || placeholder}</span>
        <Pencil className="size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </Button>
    </div>
  )
}

export { InlineEditableCellBase as InlineEditableCell }
export type { InlineEditableCellProps }
